import { Box } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import React, { useContext, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import LoadingOverlay from "../../../../../../components/common/LoadingOverlay/LoadingOverlay.tsx";
import { useAppStore } from "../../../../../../hooks/useStores.tsx";
import { defaultBrandDefinition } from "../../../../../../models/brandDefinition.ts";
import { CampaignEntityStatus } from "../../../../../../models/common.ts";
import { PanelType } from "../../../../../constants/app-options.ts";
import { DEFAULT_SCENE_PROCESSING_TIME } from "../../../../../constants/contants.ts";
import { PopUpSteps } from "../../../../../constants/mock-data.ts";
import { DesignEditorContext } from "../../../../../contexts/DesignEditor.tsx";
import useAppContext from "../../../../../hooks/useAppContext.tsx";
import { useEditor } from "../../../../../hooks/useEditor.tsx";
import useIsSidebarOpen from "../../../../../hooks/useIsSidebarOpen.tsx";
import useSetIsSidebarOpen from "../../../../../hooks/useSetIsSidebarOpen.tsx";
import { IStaticImage, LayerType, type IScene } from "../../../../../types";
import { useSmartImageUtils } from "../../../utils/smartImageUtils.ts";
import Canvas from "../../Canvas";
import Footer from "../../Footer";
import Panels from "../../Panels";
import { ObjectsEnum, SecondaryTabTypeEnum } from "../../Panels/panelItems";
import Toolbox from "../../Toolbox";
import CampaignInformation from "../CamapignInformation/CampaignInformation.tsx";
import CampaignEmptyState from "../CampaignEmptyState/CampaignEmptyState.tsx";
import CampaignResult from "../CampaignResult/CampaignResult.tsx";
import styles from "./CampaignWorkspace.module.scss";

const CampaignWorkspace = () => {
	const editor = useEditor();
	const { setActivePanel } = useAppContext();
	const isSidebarOpen = useIsSidebarOpen();
	const { resetRectangleInAGroup, findObjectFromScene, syncMissingSmartImageAndOriginalImageWithOriginalScene } =
		useSmartImageUtils();
	const setIsSidebarOpen = useSetIsSidebarOpen();
	const CAMPAIGN_INDEX = PopUpSteps.indexOf("campaigns");
	const SMART_IMAGE_INDEX = PopUpSteps.indexOf("images");
	const TEXT_INDEX = PopUpSteps.indexOf("text");
	const WIZARD_IMAGES_INDEX = PopUpSteps.indexOf("WizardImages");
	const { designEditorStore, imagesStore, brandsDefinitionStore, campaignStore, playgroundStore } = useAppStore();
	const { isPopupView, scenes, setScenes, setCurrentScene } = useContext(DesignEditorContext);
	const closeSidebar = () => setIsSidebarOpen(false);
	const openSidebar = () => setIsSidebarOpen(true);

	useEffect(() => {
		if (brandsDefinitionStore.brands.items.length === 0 && !brandsDefinitionStore.isLoading) {
			brandsDefinitionStore.loadBrandsDefinition();
		}
		designEditorStore.setProperty("campaignUpdatedTemplate", undefined);
		campaignStore.setProperty("isEditingAllAds", true);
		brandsDefinitionStore.setProperty("globalSelectedBrand", defaultBrandDefinition);
		brandsDefinitionStore.setProperty("selectedBrand", defaultBrandDefinition);
		designEditorStore.setProperty("editorStepperDisabledSteps", undefined);

		return () => {
			openSidebar();
			designEditorStore.setProperty("activeStep", 0);
			designEditorStore.setProperty("newStep", 0);
			brandsDefinitionStore.setProperty("globalSelectedBrand", defaultBrandDefinition);
			campaignStore.handleSetSelectedWizardTemplate(null);
			campaignStore.setProperty("isEditingAllAds", false);
			designEditorStore.setProperty("imageTypeToNewUrlMap", {});
			designEditorStore.setProperty("backupOfReplacedStaticImagesLayers", []);
			designEditorStore.setProperty("isSceneFullyLoaded", true);
			campaignStore.setProperty("selectedWizardTemplate", null);
			campaignStore.setProperty("selectedTemplate", null);
			designEditorStore.setProperty("editorStepperDisabledSteps", undefined);
		};
	}, []);

	useEffect(() => {
		const updateWizard = async () => {
			const { newStep } = designEditorStore;
			resetEditorState();

			switch (newStep) {
				case CAMPAIGN_INDEX:
					handleCampaignStep();
					break;
				case SMART_IMAGE_INDEX:
					await handleSmartImageStep();
					break;
				case TEXT_INDEX:
					handleTextStep();
					break;
				case WIZARD_IMAGES_INDEX:
					handleWizardImagesStep();
					break;
				default:
					openSidebar();
					break;
			}

			if (newStep !== TEXT_INDEX && newStep !== WIZARD_IMAGES_INDEX) {
				setActivePanel(designEditorStore.editorStepperSteps[newStep].tabName);
			}

			if (newStep !== CAMPAIGN_INDEX) {
				designEditorStore.setProperty("hideCampaignResultContent", true);
				designEditorStore.setProperty("numberOfProcessedAdvertisements", 1);
			}

			designEditorStore.setProperty("activeStep", newStep);
		};
		updateWizard();
	}, [designEditorStore.newStep]);

	const resetEditorState = () => {
		editor?.state.setActiveObject(undefined);
		editor?.objects.select(undefined);
	};

	const handleCampaignStep = () => {
		closeSidebar();
	};

	const handleSmartImageStep = async () => {
		designEditorStore.setProperty("isLoadingSmartImageStep", true);
		openSidebar();
		await addObject();

		imagesStore.clearSmartImageForm();
		playgroundStore.setTextToGenerate("");

		resetSmartImagesHistory();
		imagesStore.setProperty("secondaryTabType", SecondaryTabTypeEnum.SMART_IMAGE);

		designEditorStore.setProperty("isLoadingSmartImageStep", false);
		designEditorStore.setProperty("removeSmartImageFromAllScenes", false);
	};

	const resetSmartImagesHistory = () => {
		if (imagesStore.smartImagesHistory.length > 1) {
			imagesStore.smartImagesHistory = imagesStore.smartImagesHistory.slice(0, 1);
		}
	};

	const handleTextStep = () => {
		openSidebar();
		setActivePanel(PanelType.INSERTTEXT);

		const staticTextObject = editor?.canvas.canvas
			.getObjects()
			.find((object: fabric.Object) => object.type === LayerType.STATIC_TEXT);

		if (staticTextObject) {
			editor?.state.setActiveObject(staticTextObject);
			editor?.canvas.canvas.setActiveObject(staticTextObject);
		}
	};

	const handleWizardImagesStep = () => {
		openSidebar();
		setActivePanel(PanelType.WIZARDIMAGES);
		const staticImageObject = editor?.canvas.canvas
			.getObjects()
			.find((object: fabric.Object) => object.type === LayerType.STATIC_IMAGE);

		if (staticImageObject) {
			editor?.state.setActiveObject(staticImageObject);
			editor?.canvas.canvas.setActiveObject(staticImageObject);
		}
	};

	const addObject = React.useCallback(async () => {
		/*
		 * This is special case for the campaign wizard,
		 * Once "Edit main object" is clicked we will retrieve the stored images and redraw them in the canvas
		 * */
		if (editor && isPopupView) {
			const _currentScene = editor.scene.exportToJSON();
			await syncMissingSmartImageAndOriginalImageWithOriginalScene(_currentScene);
			let foundSmartGroup = await findObjectFromScene(_currentScene, ObjectsEnum.SmartImage, true);
			if (foundSmartGroup?.type?.toLowerCase() !== "group") {
				foundSmartGroup = undefined;
			}
			if (foundSmartGroup) {
				const smartImage = await findObjectFromScene(_currentScene, ObjectsEnum.SmartImage);
				const smartImageUrl = (smartImage as IStaticImage)?.src;
				smartImageUrl &&
					imagesStore.smartImagesHistory.unshift({
						id: uuidv4(),
						visual_hash: "",
						url: smartImageUrl,
						seed: 0,
						sid: "",
						input_params: {
							...(smartImage?.metadata as any),
						},
					});

				// Upload and draw the original image and its background options (Expand & Replace)
				imagesStore.secondaryTabType = SecondaryTabTypeEnum.SMART_IMAGE;
				imagesStore.setProperty("smartImageBackgroundOptions", []);
				designEditorStore.setProperty("hasSmartImages", false);

				await resetRectangleInAGroup(_currentScene, ObjectsEnum.OuterRectangle);
				await resetRectangleInAGroup(_currentScene, ObjectsEnum.InnerRectangle);

				if (campaignStore.selectedWizardTemplate) {
					const updatedPreview = (await editor?.renderer.render(_currentScene)) as string;
					const updatedScene = { ..._currentScene, preview: updatedPreview, duration: 1000 };
					const updatedScenes = campaignStore.selectedWizardTemplate?.ads_json.scenes.map((scene: IScene) => {
						if (scene.id === updatedScene.id) {
							return { ...updatedScene, preview: updatedPreview };
						}
						return editor.scene.formalizeSceneAttributes(scene);
					}) as IScene[];
					setScenes(updatedScenes);
					designEditorStore.setProperty("isSceneFullyLoaded", false);
					setCurrentScene(updatedScene);
				}
			}
		}
	}, [editor]);

	const isCanvasHidden = (): boolean =>
		designEditorStore.isTemplateEmptyState ||
		!campaignStore.selectedWizardTemplate ||
		(campaignStore.selectedWizardTemplate.is_branded_template &&
			(brandsDefinitionStore.isLoading ||
				brandsDefinitionStore.isApplyingBrandConfigurations ||
				brandsDefinitionStore.selectedBrand.status === CampaignEntityStatus.PENDING));

	return (
		<Box component="div" className={styles.graphicContainer}>
			{!designEditorStore.isGenerateAdsLoading && designEditorStore.hideCampaignResultContent && <Panels />}
			<Box component="div" className={styles.flexColumn}>
				{!isPopupView && designEditorStore.activeStep != CAMPAIGN_INDEX && <Toolbox />}
				<Box
					component="div"
					className={clsx({
						[styles.graphicSubContainer]: true,
						[styles.graphicSubContainerExtension]: !isSidebarOpen,
					})}
				>
					{!designEditorStore.hideCampaignResultContent ? (
						<CampaignResult />
					) : (
						<>
							{isCanvasHidden() && <CampaignEmptyState />}
							{designEditorStore.isGenerateAdsLoading && <CampaignInformation />}
							<LoadingOverlay
								progressBarTime={DEFAULT_SCENE_PROCESSING_TIME * scenes.length}
								loading={designEditorStore.isGenerateAdsLoading}
								coverImageUrl={scenes?.[0]?.preview}
								className={clsx({
									[styles.hiddenVisibility]: isCanvasHidden(),
								})}
							>
								<>
									<Canvas id={"bria_campaign_wizard_canvas"} />
									<Box className={styles.hide}>
										<Footer />
									</Box>
								</>
							</LoadingOverlay>
						</>
					)}
				</Box>
			</Box>
		</Box>
	);
};

const ObservedComponent = observer(CampaignWorkspace);
export default ObservedComponent;
